import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectChangeEvent, MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { RatingModule } from 'primeng/rating';
import { TableLazyLoadEvent, TableModule, Table } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SearchFilter } from '../product-filter/product-filter';
import { BaseComponent } from '@app/core/bases';
import { Product, ProductFilters, Stock, StockState } from '@app/core/interfaces';
import { ProductService, SearchService, UtilityService } from '@app/core/services';
import { takeUntil } from 'rxjs';
import { BadgeModule } from 'primeng/badge';

export interface Column {
  field: string;
  header: string;
}

@Component({
  selector: 'app-product-table',
  imports: [TableModule, TagModule, ToastModule, ButtonModule, CommonModule, FormsModule, RatingModule, MultiSelectModule, IconFieldModule, InputIconModule, InputTextModule, RouterLink, PaginatorModule, BadgeModule],
  templateUrl: './product-table.html'
})
export class ProductTable extends BaseComponent {
  @ViewChild('dt') dt?: Table;
  private _filters!: SearchFilter;
  get filters(): SearchFilter {
    return this._filters;
  }
  @Input() set filters(value: SearchFilter) {
    this._filters = value;
    // If the table state was just restored from storage, don't reset pagination
    if (this.isLoadedFromStorage) {
      // Next filters change will reset pagination
      this.isLoadedFromStorage = false;
    } else {
      // reset pagination when filters change (normal flow)
      this.pageIndex = 0;
      if (this.dt) {
        this.dt.first = 0;
      }
    }
    this.getProducts();
  }

  @Output() onColumnChange = new EventEmitter<Column[]>();

  products!: Product[];

  isLoadingResults = false;
  isLoadedFromStorage = false;

  resultsLength = 0;
  pageIndex = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 20, 50];
  sort: { field: string, order: number } = {field: 'id', order: -1};
  tableFilters: { [x: string]: any } = {};
  cols!: Column[];
  selectedColumns: Column[] = [];
  expandedRows = {};

  searchTerm: string = '';

  constructor(
    private productService: ProductService,
    private activatedRoute: ActivatedRoute,
    private searchService: SearchService,
    public utilityService: UtilityService
  ) {
    super();
    // this.searchTerm = this.activatedRoute.snapshot.queryParams['search'] || '';
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['search']) {
        this.searchTerm = params['search'];
      }
    });
    this.searchService.search$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      if (search !== this.searchTerm) {
        this.searchTerm = search;
        this.getProducts();
      }
    });
  }

  ngOnInit() {
    this.cols = [
      { field: 'gamme', header: 'Gamme' },
      { field: 'categorie', header: 'Catégorie' },
      { field: 'sous_categorie', header: 'Sous-Catégorie' },
      { field: 'created_at', header: 'Date de création' },
      { field: 'updated_at', header: 'Date de modification' },
    ];
    const savedColumns = this.storageService.searchColomn as Column[] || [];
    if (savedColumns.length > 0) {
      this.selectedColumns = savedColumns;
      this.onColumnChange.emit(this.selectedColumns);
    }
  }

  public onStateRestore(state: any) {
    // Called when dt table restores state from storage
    this.isLoadedFromStorage = true;
    try {
      if (state && state.first !== undefined && state.rows) {
        this.pageIndex = state.first / state.rows;
      }
    } catch (e) {
      // ignore, justepour évité des erreurs si le state n'est pas conforme
    }
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    this.pageSize = event.rows as number;
    this.pageIndex = event.first as number / this.pageSize;
    if(event.sortField && event.sortOrder)
      this.sort = { field: event.sortField as string, order: event.sortOrder as number };
    this.getProducts();
  }

  getProducts() {
    this.isLoadingResults = true;

    const filter: ProductFilters = { order_by: this.sort.field, order: this.sort.order > 0 ? 'asc' : 'desc', size: this.pageSize || 10, page: (this.pageIndex + 1) || 1, };
    if (this.filters?.brands) filter.marque_id = this.filters.brands.id;
    if (this.filters?.references) filter.reference = this.filters.references;
    if (this.filters?.ids) filter.id_old = this.filters.ids;
    if (this.filters?.gammes) filter.gamme_id = this.filters.gammes.id;
    if (this.filters?.categories) filter.categorie_id = this.filters.categories.id;
    if (this.filters?.subCategories) filter.sous_categorie_id = this.filters.subCategories.id;
    if (this.filters?.states.length > 0) filter.etat = this.filters.states.map(s => s.code).join(',');
    if (
      this.filters?.priceRange.length > 0 &&
      this.filters.priceRange[0] &&
      this.filters.priceRange[1] &&
      (this.filters.priceRange[0] !== 0 || this.filters.priceRange[1] !== 1000)
    ) {
      filter.prix_stock_min = this.filters.priceRange[0];
      filter.prix_stock_max = this.filters.priceRange[1];
    }
    if (this.filters?.stocks) filter.stock_ebay = this.filters.stocks;
    if (this.filters?.quantities) filter.stock_quantite = this.filters.quantities;
    if (this.searchTerm && this.searchTerm.length > 0) filter.recherche_globale = this.searchTerm;

    filter.fields = { produits: 'id,id_old,photo_couverture,etat_ebay,marque,reference,stocks,gamme,categorie,sous_categorie,created_at,updated_at' };

    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService
      .getCollection(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.products = data.results;
          this.resultsLength = data.total;
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingResults = false;
        }
      });
  }

  getMinStock(product: Product): Stock[] {
    const stocks: Stock[] = [];
    const states: StockState[] = ['NEUF','RECONDITIONNE','OCCASION','HS']

    for (const state of states) {
      const filtered: Stock[] = product.stocks?.filter((stock: Stock) => stock.etat == state) as Stock[]
      if (filtered.length > 0) {
        const quantity = filtered.reduce((acc, stock) => acc + (stock.quantite ?? 0), 0);

        if (filtered.length == 1) {
          filtered[0]['etat_quantite'] = quantity;
          stocks.push(filtered[0]);
          continue;
        }

        const ordered = filtered.sort((s1, s2) => (s1.prix as number) - (s2.prix as number))
        ordered[0]['etat_quantite'] = quantity;
        stocks.push(ordered[0]);
      }
    }

    return stocks
  }

  collapseAll() {
    this.expandedRows = {};
  }

  getBadgeSeverity(qte: number) {
    switch (qte) {
      case 0:
        return 'danger';
      case 1:
      case 2:
      case 3:
        return 'warn';
      default:
        return 'success';
    }
  }

  onSelectedColumnChange(event: MultiSelectChangeEvent) {
    const columns = event.value as Column[];
    this.storageService.searchColomn = columns;
    this.onColumnChange.emit(columns);
  }
}
